import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles } from "lucide-react";

const useCountAnimation = (end, duration, shouldStart) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!shouldStart) return;
    let startTime;
    let animationFrame;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min(
        (currentTime - startTime) / (duration * 1000),
        1
      );
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, shouldStart]);

  return count;
};

const useIntersectionObserver = (ref) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
      }
    );
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return isVisible;
};

const BestSellingProducts = () => {
  const sectionRef = useRef(null);
  const isVisible = useIntersectionObserver(sectionRef);

  const totalProducts = useCountAnimation(2000, 2, isVisible);
  const totalOrders = useCountAnimation(5000, 2, isVisible);
  const totalSellers = useCountAnimation(800, 2, isVisible);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBestSellingProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          "https://server.enjoywiki.com/flip-server/api/homepage/best-sellers"
        );
        setProducts(response.data.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching best selling products:", err);
        setError("Failed to load best selling products");
        setLoading(false);
      }
    };

    fetchBestSellingProducts();
  }, []);

  // Function to safely display review information
  const renderReviewInfo = (product) => {
    // Check if reviews data exists and has the properties we need
    const reviewCount = product.reviews?.count || 0;

    // Get average rating and ensure it's a number
    let reviewAverage = product.reviews?.average || 0;

    // Convert to a number if it's not already one
    if (typeof reviewAverage !== "number") {
      reviewAverage = parseFloat(reviewAverage) || 0;
    }

    // Format the average rating to one decimal place if it's a valid number
    const formattedAverage = isNaN(reviewAverage)
      ? "0.0"
      : reviewAverage.toFixed(1);

    return (
      <span className="service-card-rating">
        {formattedAverage} ({reviewCount}{" "}
        {reviewCount === 1 ? "Review" : "Reviews"})
      </span>
    );
  };

  return (
    <section className="cta-area py-110" ref={sectionRef}>
      <div className="container">
        <div className="bg-darkGreen cta-area-bg">
          <div className="row align-items-center">
            <div className="col-12">
              {/* Centered Title Section */}
              <div
                className="cta-content text-center aos-init aos-animate mb-5"
                data-aos="fade-up"
                data-aos-duration={1000}
                data-aos-easing="linear"
              >
                <h2 className="section-title-light fw-bold mb-4">
                  Best Selling Items
                </h2>
                <p
                  className="section-desc-light mb-40 mx-auto"
                  style={{ maxWidth: "800px" }}
                >
                  Discover our most popular digital assets that customers love.
                  These best-selling Items offer exceptional value and quality
                  for your projects.
                </p>
              </div>

              {/* Product Display Section */}
              {loading ? (
                <div className="mt-5 text-center">
                  <div className="spinner-border text-light" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="mt-5 text-center">
                  <p className="text-light">{error}</p>
                </div>
              ) : (
                <div className="mt-5">
                  <div className="row justify-content-center">
                    {products && products.length > 0 ? (
                      products.slice(0, 3).map((product) => (
                        <div
                          className="col-12 col-md-6 col-lg-4 mb-4"
                          key={product.productId}
                        >
                          <article>
                            <div
                              className="service-card bg-white aos-init h-100"
                              data-aos-easing="linear"
                            >
                              <div className="position-relative">
                                <Link
                                  to={`/${product.slug || "product"}/${
                                    product.productId
                                  }`}
                                >
                                  <img
                                    src={
                                      product.thumbnail || product.featuredImage
                                    }
                                    className="recently-view-card-img w-100"
                                    style={{
                                      aspectRatio: "16 / 9",
                                      objectFit: "cover",
                                    }}
                                    alt={product.title || product.productName}
                                  />
                                </Link>
                                <button className="service-card-wishlist-btn">
                                  <Sparkles size={25} />
                                </button>
                              </div>

                              <div className="service-card-content p-3">
                                <div className="d-flex align-items-center justify-content-between">
                                  <div>
                                    <h3 className="service-card-price fw-bold">
                                      ${product.price || "0.00"}
                                    </h3>
                                  </div>
                                  <div className="d-flex align-items-center gap-1">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="15"
                                      viewBox="0 0 16 15"
                                      fill="none"
                                    >
                                      <path
                                        d="M16 5.95909C15.8855 6.07153 15.7709 6.21207 15.6564 6.32451C14.4537 7.36454 13.2511 8.37646 12.0484 9.38838C11.9339 9.47271 11.9053 9.55704 11.9625 9.69758C12.3348 11.2717 12.707 12.8739 13.0793 14.448C13.1365 14.6448 13.1079 14.8134 12.9361 14.9258C12.7643 15.0383 12.5925 15.0102 12.4207 14.9258C10.989 14.0826 9.58587 13.2393 8.15415 12.396C8.03961 12.3117 7.9537 12.3117 7.83917 12.396C6.43607 13.2393 5.00435 14.0826 3.60126 14.8977C3.48672 14.9821 3.34355 15.0102 3.20038 14.9821C2.9713 14.9258 2.85676 14.701 2.91403 14.448C3.14311 13.5204 3.34355 12.5928 3.57262 11.6652C3.74443 10.9906 3.8876 10.316 4.05941 9.64136C4.08805 9.52893 4.05941 9.47271 3.97351 9.38838C2.74222 8.34835 1.53957 7.30832 0.308291 6.26829C0.251022 6.21207 0.193753 6.18396 0.165118 6.12775C0.0219457 6.01531 -0.0353233 5.87477 0.0219457 5.678C0.0792147 5.50935 0.222387 5.42502 0.394194 5.39691C0.651905 5.36881 0.909615 5.3407 1.19596 5.3407C2.36998 5.22826 3.54399 5.14393 4.74664 5.0315C4.97572 5.00339 5.20479 4.97528 5.43387 4.97528C5.54841 4.97528 5.60567 4.91906 5.63431 4.83474C6.2929 3.31685 6.92286 1.82708 7.58146 0.309198C7.66736 0.140545 7.75326 0.0281089 7.9537 0C8.18278 0.0562179 8.32595 0.140545 8.41186 0.365416C8.75547 1.15247 9.09908 1.96762 9.4427 2.75467C9.75768 3.4574 10.044 4.18823 10.359 4.89095C10.3876 4.97528 10.4449 5.0315 10.5594 5.0315C11.4757 5.11583 12.3921 5.17204 13.337 5.25637C14.0815 5.31259 14.8546 5.39691 15.5991 5.45313C15.7996 5.48124 15.9141 5.59368 16 5.76233C16 5.81855 16 5.90288 16 5.95909Z"
                                        fill="currentColor"
                                      />
                                    </svg>
                                    {renderReviewInfo(product)}
                                  </div>
                                </div>
                                <h3 className="service-card-title fw-semibold mt-2">
                                  <Link
                                    to={`/${product.slug || "product"}/${
                                      product.productId
                                    }`}
                                  >
                                    {product.title ||
                                      product.productName ||
                                      "Product Title"}
                                  </Link>
                                </h3>
                                <div className="d-flex align-items-center service-card-author mt-2">
                                  <img
                                    src={
                                      product.seller?.profileURL ||
                                      "/api/placeholder/40/40"
                                    }
                                    className="service-card-author-img"
                                    alt=""
                                  />
                                  <Link
                                    to={`/profile-seller/${
                                      product.seller?.id ||
                                      product.userId ||
                                      "seller"
                                    }`}
                                    className="service-card-author-name"
                                  >
                                    {product.seller?.name ||
                                      product.userName ||
                                      "Seller"}
                                  </Link>
                                </div>
                              </div>
                            </div>
                          </article>
                        </div>
                      ))
                    ) : (
                      <div className="col-12 text-center">
                        <p className="text-light">No products available</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Centered Browse All Button */}
              <div className="text-center mt-4">
                <Link to="/products" className="cta-btn-link">
                  Browse All Products
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={14}
                    height={10}
                    viewBox="0 0 14 10"
                    fill="none"
                  >
                    <path
                      d="M9 9L13 5M13 5L9 1M13 5L1 5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BestSellingProducts;
